
/**
 * Shopify Dawn - Custom Ajax Add to Cart + Cart Drawer
 *
 * Features:
 * - Prevents the normal /cart redirect.
 * - Adds the selected product/variant to the cart.
 * - Supports product pages and quick-add forms.
 * - Refreshes cart drawer sections and cart count.
 * - Removes the is-empty class when the cart contains items.
 * - Opens the cart drawer after a successful addition.
 */

(() => {
  if (window.customCartDrawerInitialized) return;
  window.customCartDrawerInitialized = true;

  const root = window.Shopify?.routes?.root || '/';
  const CART_ADD_URL = `${root}cart/add.js`;
  const CART_URL = `${root}cart.js`;

  let isAdding = false;

  // ----------------------------------------
  // Find cart components
  // ----------------------------------------

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function getCartNotification() {
    return document.querySelector('cart-notification');
  }

  function getCartComponent() {
    return getCartDrawer() || getCartNotification();
  }

  // ----------------------------------------
  // Get section IDs for Shopify rendering
  // ----------------------------------------

  function getSectionsToRender() {
    const component = getCartComponent();

    if (
      component &&
      typeof component.getSectionsToRender === 'function'
    ) {
      return component
        .getSectionsToRender()
        .map(section => section.id)
        .filter(Boolean);
    }

    return ['cart-drawer', 'cart-icon-bubble'];
  }

  // ----------------------------------------
  // Button loading state
  // ----------------------------------------

  function setButtonLoading(button, loading) {
    if (!button) return;

    button.classList.toggle('loading', loading);
    button.disabled = loading;

    const spinner = button.querySelector('.loading__spinner');

    if (spinner) {
      spinner.classList.toggle('hidden', !loading);
    }
  }

  // ----------------------------------------
  // Remove empty cart state
  // ----------------------------------------

  function updateEmptyState(cart) {
    const drawer = getCartDrawer();

    if (!drawer) return;

    const isEmpty = !cart || cart.item_count === 0;

    drawer.classList.toggle('is-empty', isEmpty);

    const drawerInner = drawer.querySelector('.drawer__inner');

    if (drawerInner) {
      drawerInner.classList.toggle('is-empty', isEmpty);
    }

    const drawerFooter = drawer.querySelector('.drawer__footer');

    if (drawerFooter) {
      drawerFooter.classList.toggle('is-empty', isEmpty);
    }
  }

  // ----------------------------------------
  // Refresh cart count bubble
  // ----------------------------------------

  function refreshCartCount(cart) {
    const count = cart?.item_count || 0;

    const bubble = document.querySelector('.cart-count-bubble');

    if (bubble) {
      bubble.innerHTML = `
        <span aria-hidden="true">${count}</span>
        <span class="visually-hidden">
          ${count} ${count === 1 ? 'item' : 'items'}
        </span>
      `;

      bubble.classList.toggle('hidden', count === 0);
    }

    const cartIconBubble = document.querySelector(
      '#shopify-section-cart-icon-bubble'
    );

    if (cartIconBubble) {
      const iconBubble = cartIconBubble.querySelector(
        '.cart-count-bubble'
      );

      if (iconBubble) {
        iconBubble.classList.toggle('hidden', count === 0);
      }
    }
  }

  // ----------------------------------------
  // Render updated Shopify sections
  // ----------------------------------------

  function renderCartSections(data) {
    const drawer = getCartDrawer();
    const notification = getCartNotification();

    if (drawer && typeof drawer.renderContents === 'function') {
      drawer.renderContents(data);
      return true;
    }

    if (
      notification &&
      typeof notification.renderContents === 'function'
    ) {
      notification.renderContents(data);
      return true;
    }

    return false;
  }

  // ----------------------------------------
  // Open cart drawer
  // ----------------------------------------

  function openCartDrawer() {
    const drawer = getCartDrawer();

    if (drawer) {
      drawer.classList.remove('is-empty');

      const drawerInner = drawer.querySelector('.drawer__inner');

      if (drawerInner) {
        drawerInner.classList.remove('is-empty');
      }

      if (typeof drawer.open === 'function') {
        drawer.open();
        return true;
      }

      // Fallback for themes without the Dawn open() method.
      drawer.classList.add('active');
      drawer.setAttribute('open', '');
      document.body.classList.add('overflow-hidden');

      return true;
    }

    const notification = getCartNotification();

    if (
      notification &&
      typeof notification.open === 'function'
    ) {
      notification.open();
      return true;
    }

    console.warn(
      'Cart drawer/notification component was not found.'
    );

    return false;
  }

  // ----------------------------------------
  // Add product to cart
  // ----------------------------------------

  async function addToCart(form, button) {
    if (isAdding) return;

    isAdding = true;
    setButtonLoading(button, true);

    try {
      const formData = new FormData(form);

      // Request updated drawer and cart icon sections.
      const sectionIds = getSectionsToRender();

      formData.set('sections', sectionIds.join(','));
      formData.set('sections_url', window.location.pathname);

      const response = await fetch(CART_ADD_URL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });

      const itemData = await response.json();

      if (!response.ok) {
        throw new Error(
          itemData.description ||
          itemData.message ||
          'Unable to add this product to your cart.'
        );
      }

      // Fetch the latest cart state.
      const cartResponse = await fetch(CART_URL, {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        }
      });

      if (!cartResponse.ok) {
        throw new Error('Unable to refresh your cart.');
      }

      const cart = await cartResponse.json();

      // Render the updated cart sections.
      const rendered = renderCartSections({
        ...itemData,
        item_count: cart.item_count,
        sections: itemData.sections || {}
      });

      // Update count and empty state after rendering.
      refreshCartCount(cart);
      updateEmptyState(cart);

      // Open the drawer only when the cart has items.
      if (cart.item_count > 0) {
        openCartDrawer();
      }

      // Notify other theme scripts or integrations.
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: {
            cart: cart,
            item: itemData
          }
        })
      );

      if (!rendered) {
        console.warn(
          'Cart was updated, but no compatible drawer renderer was found.'
        );
      }

    } catch (error) {
      console.error('Add to cart error:', error);

      alert(
        error.message ||
        'Something went wrong. Please try again.'
      );

    } finally {
      setButtonLoading(button, false);
      isAdding = false;
    }
  }

  // ----------------------------------------
  // Intercept all standard Add to Cart forms
  // ----------------------------------------

  document.addEventListener(
    'submit',
    event => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) return;

      if (!form.matches('form[action*="/cart/add"]')) {
        return;
      }

      // Do not intercept checkout or accelerated payment buttons.
      const submitter = event.submitter;

      if (
        submitter &&
        (
          submitter.name === 'checkout' ||
          submitter.classList.contains(
            'shopify-payment-button__button'
          )
        )
      ) {
        return;
      }

      // Prevent the theme's normal cart redirect handler.
      event.preventDefault();
      event.stopImmediatePropagation();

      const button =
        submitter ||
        form.querySelector('[type="submit"][name="add"]') ||
        form.querySelector('[type="submit"]');

      addToCart(form, button);
    },
    true
  );

})();